import { TERRACO_BRONZE_SALON_ID } from "./terracoBronzeBooking";

export const WEEKDAY_LABELS_PT_BR = [
  "domingo",
  "segunda-feira",
  "terça-feira",
  "quarta-feira",
  "quinta-feira",
  "sexta-feira",
  "sábado",
] as const;

export type WeeklyPromotionLike = {
  title: string;
  serviceIds?: string | null;
  eligibleWeekdays?: string | null;
  minimumParticipants?: number | null;
  offerPrice?: string | number | null;
};

export type PromotionServiceLike = {
  id: number;
  name: string;
  isActive?: boolean | null;
};

function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function parseEligibleWeekdays(raw: string | null | undefined): number[] | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const unique = Array.from(new Set(parsed.map(Number)))
      .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6)
      .sort((a, b) => a - b);
    return unique.length > 0 ? unique : null;
  } catch {
    return null;
  }
}

export function getBrasiliaWeekday(now: Date = new Date()): number {
  const label = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Sao_Paulo",
    weekday: "short",
  }).format(now);
  const weekdays: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  return weekdays[label] ?? now.getDay();
}

export function isPromotionEligibleToday(
  promotion: Pick<WeeklyPromotionLike, "eligibleWeekdays">,
  now: Date = new Date(),
): boolean {
  const days = parseEligibleWeekdays(promotion.eligibleWeekdays);
  return !days || days.includes(getBrasiliaWeekday(now));
}

export function formatEligibleWeekdays(raw: string | null | undefined): string | null {
  const days = parseEligibleWeekdays(raw);
  if (!days) return null;
  return days.map((day) => WEEKDAY_LABELS_PT_BR[day]).join(", ");
}

function parsePromotionServiceIds(raw: string | null | undefined): number[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? Array.from(new Set(parsed.map(Number))).filter((id) => Number.isInteger(id) && id > 0)
      : [];
  } catch {
    return [];
  }
}

export function isGenericBronzeRequest(message: string): boolean {
  const text = normalize(message);
  if (!/\bbronze(?:amento)?\b/.test(text)) return false;
  return !/\b(?:solar|no sol|ao sol|paredao|metodo ep|ep|cabine|cama|deitado|jato|gelado|tradicional)\b/.test(text);
}

function isSolarService(service: PromotionServiceLike): boolean {
  const name = normalize(service.name);
  return name.includes("bronzeamento no sol") || name.includes("bronze solar");
}

function isEpService(service: PromotionServiceLike): boolean {
  const name = normalize(service.name);
  return name.includes("metodo ep") || /\bep\b/.test(name);
}

function formatCurrency(value: string | number): string | null {
  const amount = Number(value);
  return Number.isFinite(amount) && amount >= 0
    ? `R$ ${amount.toFixed(2).replace(".", ",")}`
    : null;
}

/**
 * Oferta comercial curta e determinística para o Terraço-Bronze.
 * Não agenda, não promete vaga e não aplica desconto financeiro: apenas apresenta
 * modalidades cadastradas e uma campanha vigente para o dia atual.
 */
export function buildTerracoBronzeGenericOffer(input: {
  salonId: number;
  message: string;
  services: PromotionServiceLike[];
  activePromotions: WeeklyPromotionLike[];
}): string | null {
  if (input.salonId !== TERRACO_BRONZE_SALON_ID || !isGenericBronzeRequest(input.message)) {
    return null;
  }

  const bronzeServices = input.services.filter((service) => service.isActive !== false && normalize(service.name).includes("bronze"));
  const epService = bronzeServices.find(isEpService);
  if (!epService) return null;

  const alternatives = bronzeServices
    .filter((service) => service.id !== epService.id)
    .slice(0, 4)
    .map((service) => service.name);

  const solarService = bronzeServices.find(isSolarService);
  const friendsCampaign = solarService
    ? input.activePromotions.find((promotion) => {
        const serviceIds = parsePromotionServiceIds(promotion.serviceIds);
        return serviceIds.includes(solarService.id)
          && Number(promotion.minimumParticipants ?? 1) >= 2
          && Number.isFinite(Number(promotion.offerPrice));
      })
    : undefined;

  const campaignText = friendsCampaign && solarService
    ? (() => {
        const price = formatCurrency(friendsCampaign.offerPrice!);
        return price
          ? ` E hoje também está ativa a campanha ${friendsCampaign.title}: no ${solarService.name}, você e uma amiga pagam ${price} cada.`
          : "";
      })()
    : "";
  const alternativesText = alternatives.length > 0
    ? ` Depois, se preferir, também temos ${alternatives.join(", ")}.`
    : "";

  return `Claro! Para começar, posso te apresentar o ${epService.name}, nossa primeira opção de bronze.${alternativesText}${campaignText} Qual dessas opções combina melhor com você?`;
}
